import { useTranslation } from 'react-i18next'
import { useHomeData } from './hooks/useHomeData'
import { useProjectsFilter } from './hooks/useProjectsFilter'
import { GreetingSection } from './components/GreetingSection'
import { OngoingProjectsList } from './components/OngoingProjectsList'
import { ProjectsSearchFilter } from './components/ProjectsSearchFilter'
import { ProjectsTable } from './components/ProjectsTable'
import { ProjectsPagination } from './components/ProjectsPagination'
import { Button } from '@/components/ui/button'
import { sortByUrgency } from './home.logic'
import { useNavigate } from 'react-router-dom'

export function HomePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data, isLoading, error } = useHomeData()

  const {
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    currentPage,
    setCurrentPage,
    paginatedProjects,
    totalPages,
    totalCount,
  } = useProjectsFilter(data?.allProjects ?? [])

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="border-primary mb-2 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
          <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <p className="text-destructive">{t('errors.loadingFailed')}</p>
          <p className="text-muted-foreground text-sm">{error.message}</p>
        </div>
      </div>
    )
  }

  if (!data) return null

  const sortedOngoingProjects = sortByUrgency(data.ongoingProjects)

  return (
    <div>
      <GreetingSection ongoingProjects={data.ongoingProjects} />

      <OngoingProjectsList projects={sortedOngoingProjects} />

      <div>
        {/* Section Header */}
        <div className="mb-3 flex items-baseline gap-2">
          <h2 className="text-foreground text-base font-bold">
            {t('home.allFinancialStatements')}
          </h2>
          <span className="text-muted-foreground text-xs">{t('home.allProjects')}</span>
          <span className="bg-primary/10 text-primary ml-auto rounded-full px-2 py-0.5 text-xs font-semibold">
            {totalCount} {t('home.projectsUnit')}
          </span>
        </div>

        {/* Projects Container */}
        <div className="border-border bg-card text-card-foreground overflow-hidden rounded-lg border">
          <ProjectsSearchFilter
            search={search}
            onSearchChange={setSearch}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />

          <ProjectsTable projects={paginatedProjects} />

          <ProjectsPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={8}
            onPageChange={setCurrentPage}
          />
        </div>
        {/* Button to create a new project */}
        <div className="mt-8 flex justify-center pb-8">
          <Button
            onClick={() => void navigate('/projects/new')}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-6"
          >
            + {t('project.createNew')}
          </Button>
        </div>
      </div>
    </div>
  )
}
